import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    setToken(params.get("token") || "");
    // remove the token from the address bar
    window.history.replaceState({}, document.title, "/reset-password");
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 1600);
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="You're all set.">
        <div data-testid="reset-done" className="flex gap-3 text-sm text-stone-700 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
          <span>Your password has been changed. Redirecting you to sign in…</span>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password you don't use elsewhere.">
      {!token ? (
        <div data-testid="reset-notoken" className="flex gap-3 text-sm text-stone-700 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <WarningCircle size={20} weight="fill" className="text-amber-600 shrink-0" />
          <span>This reset link is missing its token. Please request a new one from <Link to="/forgot-password" className="text-[#C2410C] underline">forgot password</Link>.</span>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">New password</Label>
            <PasswordInput testid="reset-password" required value={pw}
              onChange={(e) => setPw(e.target.value)} className="mt-1 rounded-xl" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Confirm password</Label>
            <PasswordInput testid="reset-confirm" required value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-xl" placeholder="Re-enter password" />
          </div>
          {error && <div data-testid="reset-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl">{error}</div>}
          <Button data-testid="reset-submit" type="submit" disabled={loading}
            className="w-full rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
            {loading ? "Updating…" : "Set password"} <ArrowRight size={16} />
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
